import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const AZUL = [37, 99, 235];

const soData = (iso) => (iso || "").split("T")[0];
const hora = (iso) => ((iso || "").split("T")[1] || "").slice(0, 5);
const dataBR = (d) => {
  if (!d) return "-";
  const [y, m, day] = String(d).split("-");
  return day ? `${day}/${m}/${y}` : d;
};
const brl = (v) => `R$ ${(Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TIPO_LABEL = {
  anotacao_enfermagem: "Anotação de Enfermagem",
  ocorrencia: "Ocorrência",
  evolucao: "Evolução",
};

function cabecalho(doc, empresa, titulo, sub) {
  doc.setFillColor(...AZUL);
  doc.rect(0, 0, 210, 26, "F");
  let tx = 14;
  if (empresa?.logo) {
    try { doc.addImage(empresa.logo, "PNG", 12, 4, 18, 18); tx = 34; } catch (e) { /* ignore */ }
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(empresa?.nome || "Casa de Repouso São Jorge", tx, 12);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`CNPJ: ${empresa?.cnpj || "-"}`, tx, 18);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(titulo, 196, 12, { align: "right" });
  if (sub) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.text(sub, 196, 18, { align: "right" });
  }
  doc.setTextColor(30, 41, 59);
}

function secao(doc, y, titulo) {
  if (y > 268) { doc.addPage(); y = 20; }
  doc.setFillColor(241, 245, 249);
  doc.rect(14, y - 5, 182, 7, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 41, 59);
  doc.text(titulo, 16, y);
  return y + 6;
}

function tabela(doc, y, head, body, opts = {}) {
  autoTable(doc, {
    startY: y,
    head: [head],
    body: body.length ? body : [["—", ...head.slice(1).map(() => "")]],
    theme: "striped",
    headStyles: { fillColor: AZUL, fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6, valign: "top" },
    margin: { left: 14, right: 14 },
    ...opts,
  });
  return doc.lastAutoTable.finalY + 8;
}

// ============================================================
// RELATÓRIO CLÍNICO (sem informações financeiras)
// ============================================================
export function gerarRelatorioClinicoPDF({ empresa, residente, registros }) {
  const doc = new jsPDF();
  const hoje = new Date().toLocaleDateString("pt-BR");
  cabecalho(doc, empresa, "RELATÓRIO CLÍNICO", `Emitido em ${hoje}`);
  let y = 34;

  // ---------- 1. DADOS DO RESIDENTE (clínicos) ----------
  y = secao(doc, y, "1. Dados do Residente");
  const statusTxt = (residente?.status || "Ativo") === "Ativo" ? "Ativo" : "Inativo";
  const linhasDados = [
    ["Nome", residente?.nome || "-", "Quarto", residente?.quarto || "-"],
    ["CPF", residente?.cpf || "-", "CNS", residente?.cns || "-"],
    ["Nascimento", dataBR(residente?.dataNascimento), "Internação", dataBR(residente?.dataInternacao)],
    ["Grau de dependência", residente?.grauDependencia || "-", "Status", statusTxt],
    ["Mobilidade", residente?.mobilidade || "-", "Dieta", residente?.dieta || "-"],
    ["Diagnósticos (CID)", residente?.diagnosticos || "-", "Alergias", residente?.alergias || "-"],
  ];
  if (residente?.observacoesClinicas) {
    linhasDados.push(["Observações clínicas", residente.observacoesClinicas, "", ""]);
  }
  y = tabela(doc, y, ["Campo", "Valor", "Campo", "Valor"], linhasDados, {
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 34 }, 2: { fontStyle: "bold", cellWidth: 34 } },
    headStyles: { fillColor: [148, 163, 184], fontSize: 8.5 },
  });

  // ---------- Responsável ----------
  const fams = residente?.familiares || [];
  const resp = fams.find((f) => f.responsavelFinanceiro) || fams[0] || null;
  const respNome = resp?.nome || residente?.responsavel || "-";
  const respTel = resp?.telefone || residente?.telefone || "-";
  const respCpf = resp?.cpf || residente?.responsavelCpf || "-";
  const respParent = resp?.parentesco || residente?.grauParentesco || "-";
  const respEndereco = resp
    ? ([resp.endLogradouro, resp.endNumero, resp.endComplemento, resp.endBairro, resp.endCidade, resp.endUf].filter(Boolean).join(", ")
      + (resp.endCep ? ` - CEP ${resp.endCep}` : "")) || "-"
    : "-";
  y = secao(doc, y, "1.1 Responsável");
  y = tabela(doc, y, ["Campo", "Valor", "Campo", "Valor"], [
    ["Nome", respNome, "Parentesco", respParent],
    ["Telefone", respTel, "CPF", respCpf],
    ["Endereço", respEndereco, "", ""],
  ], {
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 34 }, 2: { fontStyle: "bold", cellWidth: 34 } },
    headStyles: { fillColor: [148, 163, 184], fontSize: 8.5 },
  });
  y = secao(doc, y, "2. Anotações, Ocorrências e Evoluções");
  const textuais = (registros || [])
    .filter((r) => ["anotacao_enfermagem", "ocorrencia", "evolucao"].includes(r.tipo))
    .sort((a, b) => (a.data || "").localeCompare(b.data || ""));

  if (textuais.length === 0) {
    doc.setFont("helvetica", "italic"); doc.setFontSize(9); doc.setTextColor(148, 163, 184);
    doc.text("Nenhum registro.", 16, y); y += 8;
    doc.setTextColor(30, 41, 59);
  } else {
    const porDia = {};
    textuais.forEach((r) => { (porDia[soData(r.data)] ||= []).push(r); });
    Object.keys(porDia).sort().forEach((dia) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(71, 85, 105);
      doc.text(dataBR(dia), 16, y); y += 4.8;
      porDia[dia].forEach((r) => {
        const d = r.dados || {};
        const label = TIPO_LABEL[r.tipo] || r.tipo;
        const extra = r.tipo === "ocorrencia" ? ` (${d.gravidade || "-"})`
          : r.tipo === "evolucao" && d.categoria ? ` · ${d.categoria}` : "";
        const titulo = `${label}${extra} · ${hora(r.data)}`;
        const linhas = doc.splitTextToSize(d.texto || "-", 176);
        const bloco = 5 + linhas.length * 4.4 + 5;
        if (y + bloco > 285) { doc.addPage(); y = 20; }
        doc.setFont("helvetica", "bold"); doc.setFontSize(8.5); doc.setTextColor(37, 99, 235);
        doc.text(titulo, 18, y); y += 4.4;
        doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(51, 65, 85);
        doc.text(linhas, 18, y); y += linhas.length * 4.4 + 0.8;
        doc.setFontSize(7.5); doc.setTextColor(148, 163, 184);
        doc.text(r.autor || "-", 18, y); y += 5.5;
      });
      y += 1;
    });
    doc.setTextColor(30, 41, 59);
  }

  // ---------- 3. SINAIS VITAIS ----------
  y = secao(doc, y, "3. Sinais Vitais");
  const sv = (registros || [])
    .filter((r) => r.tipo === "sinais_vitais")
    .sort((a, b) => (b.data || "").localeCompare(a.data || ""));
  y = tabela(doc, y, ["Data/Hora", "PA", "FC", "FR", "Temp", "SpO2", "Glicemia", "Dor", "Peso"],
    sv.map((r) => {
      const d = r.dados || {};
      const pa = (d.pas || d.pad) ? `${d.pas || "-"}x${d.pad || "-"}` : "-";
      return [
        `${dataBR(soData(r.data))} ${hora(r.data)}`, pa,
        d.fc || "-", d.fr || "-", d.temp || "-", d.spo2 ? `${d.spo2}%` : "-",
        d.glicemia || "-", d.dor || "-", d.peso || "-",
      ];
    }), { styles: { fontSize: 8, cellPadding: 1.4 } });

  doc.save(`relatorio_clinico_${(residente?.nome || "residente").replace(/\s+/g, "_")}.pdf`);
}

// ============================================================
// RELATÓRIO FINANCEIRO (separado, sem dados clínicos)
// ============================================================
export function gerarRelatorioFinanceiroPDF({ empresa, residente, faturas }) {
  const doc = new jsPDF();
  const hoje = new Date().toLocaleDateString("pt-BR");
  cabecalho(doc, empresa, "RELATÓRIO FINANCEIRO", `Emitido em ${hoje}`);
  let y = 34;

  y = secao(doc, y, "1. Identificação");
  y = tabela(doc, y, ["Campo", "Valor", "Campo", "Valor"], [
    ["Nome", residente?.nome || "-", "Quarto", residente?.quarto || "-"],
    ["CPF", residente?.cpf || "-", "Responsável", residente?.responsavel || "-"],
    ["Início do contrato", dataBR(residente?.dataInicioContrato), "Mensalidade", brl(residente?.valorMensalidade)],
  ], {
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 34 }, 2: { fontStyle: "bold", cellWidth: 34 } },
    headStyles: { fillColor: [148, 163, 184], fontSize: 8.5 },
  });

  // Histórico financeiro
  y = secao(doc, y, "2. Histórico Financeiro");
  const fats = (faturas || []).slice().sort((a, b) => (a.mes || "").localeCompare(b.mes || ""));
  y = tabela(doc, y, ["Mês", "Descrição", "Total", "Situação"],
    fats.map((f) => {
      const desc = [
        ...(f.extras || []).map((e) => e.descricao),
        ...((f.itens || []).length ? [`${f.itens.length} produto(s) consumido(s)`] : []),
      ].join(" · ") || "-";
      return [f.mes, desc, brl(f.total), f.paga ? "Paga" : "Em aberto"];
    }), { columnStyles: { 1: { cellWidth: 96 }, 2: { halign: "right" } } });

  // Informe anual de rendimentos (só faturas pagas: mensalidade + 13º; produtos fora)
  y = secao(doc, y, "3. Informe Anual de Rendimentos (faturas pagas)");
  doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(148, 163, 184);
  if (y > 275) { doc.addPage(); y = 20; }
  doc.text("Considera apenas mensalidade e 13º de faturas pagas. Produtos consumidos não entram no informe.", 16, y);
  y += 6; doc.setTextColor(30, 41, 59);

  const porAno = {};
  fats.filter((f) => f.paga).forEach((f) => {
    const ano = (f.mes || "").slice(0, 4);
    if (!ano) return;
    if (!porAno[ano]) porAno[ano] = { mensalidade: 0, decimo: 0 };
    [...(f.extras || []), ...((f.manual || []).filter((m) => m.parte === "mensal"))].forEach((e) => {
      const d = (e.descricao || "").toLowerCase();
      if (d.includes("13")) porAno[ano].decimo += Number(e.valor) || 0;
      else if (d.includes("mensalidade")) porAno[ano].mensalidade += Number(e.valor) || 0;
    });
  });
  const anos = Object.keys(porAno).sort();
  y = tabela(doc, y, ["Ano", "Mensalidade paga", "13º pago", "Total do ano"],
    anos.map((a) => {
      const v = porAno[a];
      return [a, brl(v.mensalidade), brl(v.decimo), brl(v.mensalidade + v.decimo)];
    }), {
      columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right", fontStyle: "bold" } },
    });

  doc.save(`relatorio_financeiro_${(residente?.nome || "residente").replace(/\s+/g, "_")}.pdf`);
}
